/**
 * Configuración centralizada de la API
 * Usa variables de entorno para configuración flexible
 */

const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 10000,
  dashboardRefreshInterval: parseInt(process.env.REACT_APP_DASHBOARD_REFRESH_INTERVAL) || 300000,
};

/**
 * Endpoints de la API
 */
export const API_ENDPOINTS = {
  // Autenticación
  auth: {
    login: '/api/auth/login/',
    refresh: '/api/auth/refresh/',
  },
  
  // Dashboard
  dashboard: '/api/dashboard/',
  
  // Recursos
  farmacias: '/api/farmacias/',
  productos: '/api/productos/',
  inventario: '/api/inventario/',
  ventas: '/api/ventas/',
  bancos: '/api/bancos/',
  
  // Health check
  health: '/api/health/',
};

/**
 * Construye la URL completa para un endpoint
 * @param {string} endpoint - El endpoint relativo
 * @param {object} params - Parámetros de query string
 * @returns {string} URL completa
 */
export const buildURL = (endpoint, params = {}) => {
  // Si el endpoint ya es una URL completa, usarla directamente
  let fullURL;
  if (endpoint.startsWith('http')) {
    fullURL = endpoint;
  } else {
    // Asegurar que baseURL no termine con / y endpoint empiece con /
    const base = API_CONFIG.baseURL.replace(/\/$/, '');
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    fullURL = `${base}${path}`;
  }
  
  // Agregar parámetros de query string
  const url = new URL(fullURL);
  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined) {
      url.searchParams.append(key, params[key]);
    }
  });
  
  return url.toString();
};

/**
 * Obtiene el token de autenticación del localStorage
 * @returns {string|null} Token de autenticación
 */
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Guarda el token de autenticación en localStorage
 * @param {string} token - Token de autenticación
 */
export const setAuthToken = (token) => {
  localStorage.setItem('token', token);
};

/**
 * Elimina el token de autenticación del localStorage
 */
export const removeAuthToken = () => {
  localStorage.removeItem('token');
};

/**
 * Obtiene los headers por defecto para las peticiones
 * @returns {object} Headers
 */
export const getDefaultHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * Realiza una petición a la API
 * @param {string} endpoint - Endpoint de la API
 * @param {object} options - Opciones de fetch
 * @returns {Promise} Promesa con la respuesta
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : buildURL(endpoint);
  
  const config = {
    ...options,
    headers: {
      ...getDefaultHeaders(),
      ...options.headers,
    },
    timeout: API_CONFIG.timeout,
  };
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);
    
    const response = await fetch(url, {
      ...config,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    // Si la respuesta no es OK, lanzar error
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }
    
    // Intentar parsear como JSON
    const data = await response.json();
    return data;
    
  } catch (error) {
    // Si es error de timeout
    if (error.name === 'AbortError') {
      throw new Error('La petición ha excedido el tiempo de espera');
    }
    
    // Si es error de red
    if (error.message === 'Failed to fetch') {
      throw new Error('Error de conexión. Verifica tu conexión a internet.');
    }
    
    throw error;
  }
};

/**
 * Métodos HTTP simplificados
 */
export const api = {
  get: (endpoint, params = {}) => {
    const url = buildURL(endpoint, params);
    return apiRequest(url, { method: 'GET' });
  },
  
  post: (endpoint, data = {}) => {
    return apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  put: (endpoint, data = {}) => {
    return apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  patch: (endpoint, data = {}) => {
    return apiRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  
  delete: (endpoint) => {
    return apiRequest(endpoint, { method: 'DELETE' });
  },
};

export default API_CONFIG;

// Made with Bob
